import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CSTop from '../CS/CSTop';
import InputSearch from '../common/InputSearch';
import HButton from '../common/HButton';
import styled from 'styled-components';
import DevicePagination from '../registration/DevicePagination';
import DropdownList from '../auth/styled/DropdownList';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { listReleases, setDefaultVersionRelease } from '../../modules/cs';
import Spinner from '../common/Spinner';

const Content = styled.div`
  position: relative;
  margin: 0 auto;
  width: 1250px;
  min-width: 1250px;
  padding: 64px 40px 40px 40px;

  table.device {
    border-collapse: collapse;
    text-align: center;
    line-height: 1.5;
    width: 100%;
    margin-bottom: 32px;
    color: #212529;
  }

  table.device thead {
    background-color: white;
  }
  table.device thead th {
    min-width: 50px;
  }
  table.device th {
    font-family: 'Source Sans Pro';
    font-weight: 600;
    font-size: 15px;
    color: #59616a;
    vertical-align: middle !important;
  }

  table.device td,
  table.device th {
    padding: 0.75rem;
    border: 1px solid #e7e9ea;
  }

  table.device tbody {
    vertical-align: middle;
  }
  table.device td {
    font-family: 'Source Sans Pro';
    font-size: 15px;
    color: rgba(48, 57, 69, 0.8);
  }

  /* Checkbox avec images SVG (style CheckVersionPage) */
  .checkboxDiv {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
  }

  .default-checkbox {
    background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checkbox-active.svg);
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 24px;
    height: 24px;
    margin: 0;
    outline: none;
    cursor: pointer;

    &:checked {
      background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checkbox-selected.svg);
    }
    &:disabled {
      background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checkbox-disabled.svg);
      cursor: not-allowed;
    }
    &:checked:disabled {
      background-image: url(${process.env.PUBLIC_URL}/images/settings/icon-24-checked-disabled.svg);
    }
  }
`;

const TableTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Source Sans Pro';
  font-weight: 600;
  font-size: 25px;
  color: #59616a;
  margin-bottom: 24px;
`;

const Notification = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  min-width: 300px;
  max-width: 500px;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  
  &.success {
    background-color: #4CAF50;
    color: white;
  }
  
  &.error {
    background-color: #f44336;
    color: white;
  }
  
  &.info {
    background-color: #2196F3;
    color: white;
  }
  
  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(400px);
      opacity: 0;
    }
  }
  
  &.closing {
    animation: slideOut 0.3s ease-in;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const VersioningRelease = () => {
  const dispatch = useDispatch();
  const { versionReleases, loading, error, setDefaultResult } = useSelector(({ cs, loading }) => ({
    versionReleases: cs.versionReleases,
    loading: loading['cs/LIST_RELEASES'] || loading['cs/SET_DEFAULT_VERSION'],
    error: cs.versionReleasesError,
    setDefaultResult: cs.setDefaultVersionResult,
  }));

  const [search, setSearch] = useState('');
  const [modelFilter, setModelFilter] = useState(null);
  const [osFilter, setOsFilter] = useState(null);
  const [gpuFilter, setGpuFilter] = useState(null);
  const [globalVersionFilter, setGlobalVersionFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [resetKey, setResetKey] = useState(0);

  const [localDefaultReleases, setLocalDefaultReleases] = useState({});
  const [notification, setNotification] = useState(null);

  const prevSetDefaultResultRef = useRef(null);
  const prevErrorRef = useRef(null);

  const releases = versionReleases ? versionReleases.results ?? [] : [];
  const pageInfo = versionReleases ? versionReleases.pageInfo ?? { count: 0, totalPages: 1 } : { count: 0, totalPages: 1 };

  const backendOptions = versionReleases?.options || {};

  const modelOptions = useMemo(
    () => ['All', ...(backendOptions.models || [])],
    [backendOptions.models]
  );
  const osOptions = useMemo(
    () => ['All', ...(backendOptions.oses || [])],
    [backendOptions.oses]
  );
  const gpuOptions = useMemo(
    () => ['All', ...(backendOptions.gpus || [])],
    [backendOptions.gpus]
  );
  const globalVersionOptions = useMemo(
    () => ['All', ...(backendOptions.globalVersions || [])],
    [backendOptions.globalVersions]
  );

  useEffect(() => {
    if (modelFilter && !modelOptions.includes(modelFilter)) setModelFilter(null);
  }, [modelOptions, modelFilter]);
  useEffect(() => {
    if (osFilter && !osOptions.includes(osFilter)) setOsFilter(null);
  }, [osOptions, osFilter]);
  useEffect(() => {
    if (gpuFilter && !gpuOptions.includes(gpuFilter)) setGpuFilter(null);
  }, [gpuOptions, gpuFilter]);
  useEffect(() => {
    if (globalVersionFilter && !globalVersionOptions.includes(globalVersionFilter)) setGlobalVersionFilter(null);
  }, [globalVersionOptions, globalVersionFilter]);

  const normalize = (v) => (!v || v === 'All' ? '' : v);

  const filters = useMemo(() => ({
    searchString: search,
    model: normalize(modelFilter),
    os: normalize(osFilter),
    gpu: normalize(gpuFilter),
    globalVersion: normalize(globalVersionFilter),
  }), [search, modelFilter, osFilter, gpuFilter, globalVersionFilter]);

  useEffect(() => {
    dispatch(listReleases({ filters, page }));
  }, [dispatch, filters, page]);

  useEffect(() => {
    setPage(1);
  }, [search, modelFilter, osFilter, gpuFilter, globalVersionFilter]);

  useEffect(() => {
    if (releases && releases.length > 0) {
      const defaultMap = {};
      releases.forEach(release => {
        if (release.isDefaultForModel || release.is_default_for_model) {
          defaultMap[release.model] = release.id;
        }
      });
      setLocalDefaultReleases(defaultMap);
    }
  }, [releases]);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    
    setTimeout(() => {
      setNotification(prev => prev ? { ...prev, closing: true } : null);
      setTimeout(() => setNotification(null), 300);
    }, 3000);
  };

  const closeNotification = () => {
    setNotification(prev => prev ? { ...prev, closing: true } : null);
    setTimeout(() => setNotification(null), 300);
  };

  const handleSetDefault = (release, isCurrentlyDefault) => {
    setLocalDefaultReleases(prev => ({
      ...prev,
      [release.model]: release.id
    }));

    dispatch(setDefaultVersionRelease({ releaseId: release.id }));
    
    showNotification(
      `Setting version ${release.globalVersion} as default for ${release.model}...`,
      'info'
    );
  };

  useEffect(() => {
    if (setDefaultResult && setDefaultResult !== prevSetDefaultResultRef.current) {
      prevSetDefaultResultRef.current = setDefaultResult;
      
      showNotification(
        `✓ ${setDefaultResult.message || 'Default version set successfully!'}`,
        'success'
      );
      
      dispatch(listReleases({ filters, page }));
    }
  }, [setDefaultResult]);

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      prevErrorRef.current = error;
      
      showNotification(
        `Error: ${error.message || 'Failed to set default version'}`,
        'error'
      );
    }
  }, [error]);

  const filteredReleases = releases;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { year:'numeric', month:'2-digit', day:'2-digit' }) : '—';

  const onSetPage = (newPage) => setPage(newPage);

  const handleReset = () => {
    setSearch('');
    setModelFilter(null);
    setOsFilter(null);
    setGpuFilter(null);
    setGlobalVersionFilter(null);
    setPage(1);
    setResetKey((k) => k + 1);
  };

  const getOptionValue = (opt) =>
    typeof opt === 'string' ? opt : (opt && (opt.value ?? opt.label)) || '';

  const handleDownloadExcel = () => {
    const data = filteredReleases.map(release => {
      return {
        'Global Version': release.globalVersion,
        'Scan3D Version': release.scan3dVersion,
        'PatientDB Version': release.patientDbVersion,
        'OS': release.os,
        'GPU': release.gpu,
        'Model': release.model,
        'Date': release.date ? new Date(release.date).toISOString().slice(0,10) : '',
        'InstallerLink': release.installerLink,
        'ChangeLogLink': release.installerLink,
        'Default': release.isDefaultForModel ? 'Yes' : 'No',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Releases');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'releases.xlsx');
  };

  if (error) {
    return (
      <>
        <CSTop />
        <Content>
          <div>Error: {error.message}</div>
        </Content>
      </>
    );
  }

  return (
    <>
      <CSTop />
      <Content>
        {/* Notification Toast */}
        {notification && (
          <Notification className={`${notification.type} ${notification.closing ? 'closing' : ''}`}>
            {notification.message}
            <CloseButton onClick={closeNotification}>×</CloseButton>
          </Notification>
        )}

        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(2px)',
              zIndex: 10,
            }}
          >
            <Spinner />
          </div>
        )}

        <TableTop>
          <div>Versioning Releases</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <HButton
              category="Primary"
              size="Medium"
              text={'Release Registration'.toUpperCase()}
              onClick={() => {
                window.location.href = '/adm/versioning/release-registration';
              }}
            />
            <HButton
              category="Tertiary"
              size="Medium"
              text={'Download Excel File'.toUpperCase()}
              onClick={handleDownloadExcel}
            />
          </div>
        </TableTop>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '16px',
            marginBottom: '24px',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <InputSearch
              key={`search-${resetKey}`}
              size="Medium"
              placeholder={'Search'}
              width="355px"
              value={search}
              onSearchEnterPress={setSearch}
              onChange={(e) => setSearch(e.target.value)}
            />

            <DropdownList
              key={`model-${resetKey}`}
              width="180px"
              height="40px"
              items={modelOptions}
              value={modelFilter ?? undefined}
              defaultValue="Model"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setModelFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />
            <DropdownList
              key={`os-${resetKey}`}
              width="180px"
              height="40px"
              items={osOptions}
              value={osFilter ?? undefined}
              defaultValue="OS"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setOsFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />
            <DropdownList
              key={`gpu-${resetKey}`}
              width="180px"
              height="40px"
              items={gpuOptions}
              value={gpuFilter ?? undefined}
              defaultValue="GPU"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setGpuFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />
            <DropdownList
              key={`gv-${resetKey}`}
              width="180px"
              height="40px"
              items={globalVersionOptions}
              value={globalVersionFilter ?? undefined}
              defaultValue="Global Version"
              onSelect={(opt) => {
                const v = getOptionValue(opt);
                setGlobalVersionFilter(v === 'All' ? null : v);
              }}
              zIndex={1}
            />
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <HButton category="Primary" size="Medium" text={'RESET'} onClick={handleReset} />
          </div>
        </div>

        <div
          style={{
            float: 'right',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'Source Sans Pro',
            fontSize: '15px',
            color: '#303945cc',
            marginBottom: '15px',
          }}
        >
          Total Releases: {pageInfo.count}
        </div>

        <table className="device" style={{ marginTop: '24px', width: '100%' }}>
          <thead>
            <tr>
              <th>Global Version</th>
              <th>Scan3D Version</th>
              <th>PatientDB Version</th>
              <th>OS</th>
              <th>GPU</th>
              <th>Model</th>
              <th>Links</th>
              <th>Date</th>
              <th style={{ width: '100px' }}>Default</th>
            </tr>
          </thead>
          <tbody>
            {releases.length > 0 ? (
              releases.map((release) => {
                const isDefault = localDefaultReleases[release.model] === release.id;
                
                const links = [];
                if (release.installerLink) {
                  links.push({ url: release.installerLink, label: 'Installer' });
                }
                if (release.changelogLink) {
                  links.push({ url: release.changelogLink, label: 'Changelog' });
                }
                
                return (
                  <tr key={release.id}>
                    <td>{release.globalVersion}</td>
                    <td>{release.scan3dVersion}</td>
                    <td>{release.patientDbVersion}</td>
                    <td>{release.os}</td>
                    <td>{release.gpu}</td>
                    <td>{release.model}</td>
                    <td>
                      {links.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                          {links.map((link, index) => (
                            <a 
                              key={index}
                              href={link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#2226ffff', 
                                textDecoration: 'underline',
                                fontSize: '13px',
                                wordBreak: 'break-all'
                              }}
                              title={link.url}
                            >
                              {link.label}
                            </a>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td>{fmtDate(release.date)}</td>
                    <td>
                      <div className="checkboxDiv">
                        <input
                          type="checkbox"
                          className="default-checkbox"
                          checked={isDefault}
                          onChange={() => handleSetDefault(release, isDefault)}
                          disabled={loading}
                          title={isDefault 
                            ? `Default version for ${release.model}` 
                            : `Set as default version for ${release.model}`
                          }
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center' }}>
                  No releases found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <DevicePagination pageInfo={pageInfo} paginate={onSetPage} />
      </Content>
    </>
  );
};

export default VersioningRelease;